import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Car, Lock, User, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900 flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#C5A059] flex items-center justify-center">
            <Car className="h-6 w-6 text-slate-900" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-extrabold text-xl text-white tracking-tight">MB TRANSPORT</div>
            <div className="text-xs text-slate-400">VTC · Marseille</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-4xl font-extrabold text-white leading-tight">
            Votre facturation<br /><span className="text-[#C5A059]">VTC</span>, en toute simplicité.
          </h1>
          <p className="text-slate-400 mt-4 max-w-md text-sm leading-relaxed">
            Factures, devis, courses et tableau de bord — accès sécurisé réservé à MB TRANSPORT.
          </p>
        </div>
        <div className="text-slate-500 text-xs">SIRET 992 510 933 00011 · 30 Avenue des Olives, 13013 Marseille</div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-[#C5A059]/10 blur-3xl" />
        <div className="absolute right-20 top-20 h-64 w-64 rounded-full bg-[#C5A059]/5 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-xl bg-[#C5A059] flex items-center justify-center">
              <Car className="h-5 w-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <div className="font-display font-extrabold text-lg text-white">MB TRANSPORT</div>
          </div>
          <h2 className="font-display text-2xl font-bold text-white">Connexion</h2>
          <p className="text-slate-400 text-sm mt-1 mb-8">Identifiez-vous pour accéder au tableau de bord.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Identifiant</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
                  placeholder="Votre identifiant"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username"
                  autoFocus
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  type="password"
                  className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full bg-[#C5A059] hover:bg-[#b38f48] text-slate-900 font-semibold" data-testid="login-submit">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connexion...</> : "Se connecter"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
